import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FormField } from '../components/FormField';
import { Button } from '../components/Button';
import { SubsList } from '../components/SubsList';
import { useMatchApi } from '../api/match';
import { ApiError } from '../api/client';
import {
  MATCH_TYPES,
  MATCH_FORMATS,
  type Match,
  type MatchType,
  type MatchFormat,
  type Substitution,
  type UpdateMatchInput,
} from '../types';

const POSITIONS = ['Goalkeeper', 'Defender', 'Midfielder', 'Forward', 'Other'];

export function MatchEdit() {
  const { kidId, matchId } = useParams<{ kidId: string; matchId: string }>();
  const navigate = useNavigate();
  const api = useMatchApi();
  const [match, setMatch] = useState<Match | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Local form fields (kept simple — derived from match on load)
  const [form, setForm] = useState<UpdateMatchInput>({});
  const [subs, setSubs] = useState<Array<{ minute: string; direction: 'on' | 'off' }>>([]);
  const [positions, setPositions] = useState<string[]>([]);

  useEffect(() => {
    if (!kidId || !matchId) return;
    let cancelled = false;
    (async () => {
      try {
        const m = await api.get(kidId, matchId);
        if (!cancelled) {
          setMatch(m);
          setForm({
            date: m.date,
            kickoff: m.kickoff,
            opposition: m.opposition,
            homeAway: m.homeAway,
            matchType: m.matchType,
            matchTitle: m.matchTitle,
            matchLengthMin: m.matchLengthMin,
            ourScore: m.ourScore,
            theirScore: m.theirScore,
            kidRating: m.kidRating,
            manOfMatch: m.manOfMatch,
            captain: m.captain,
            substitutions: m.substitutions ?? [],
            matchFormat: m.matchFormat,
            note: m.note,
            minutesPlayed: m.minutesPlayed,
          });
          const initial = (m.substitutions ?? []).map((s) => ({
            minute: String(s.minute),
            direction: s.direction,
          }));
          // back-compat: synthesize subs from legacy fields if no array
          if (initial.length === 0) {
            if (typeof m.subbedOnAt === 'number') initial.push({ minute: String(m.subbedOnAt), direction: 'on' });
            if (typeof m.subbedOffAt === 'number') initial.push({ minute: String(m.subbedOffAt), direction: 'off' });
          }
          setSubs(initial);
          // Seed positions: prefer new positions[], fall back to legacy single position
          if (m.positions && m.positions.length) {
            setPositions(m.positions);
          } else if (m.position) {
            setPositions([m.position]);
          }
        }
      } catch (e) {
        if (!cancelled) setLoadError((e as Error).message || "Couldn't load match.");
      }
    })();
    return () => { cancelled = true; };
  }, [kidId, matchId, api]);

  if (loadError) {
    return (
      <div>
        <h1>Edit match</h1>
        <p style={{ color: 'var(--error)' }}>{loadError}</p>
        <Link to={`/kids/${encodeURIComponent(kidId ?? '')}`} style={{ color: 'var(--green)' }}>← Back</Link>
      </div>
    );
  }

  if (!match || !kidId || !matchId) return <p style={{ color: 'var(--muted)' }}>Loading…</p>;

  const set = <K extends keyof UpdateMatchInput>(key: K, value: UpdateMatchInput[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const patch: UpdateMatchInput = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === undefined) continue;
        if (typeof v === 'string' && v.trim() === '') continue;
        (patch as Record<string, unknown>)[k] = v;
      }
      // Build substitutions from local state — always send (even empty)
      // so an emptied list clears the server-side value.
      const builtSubs: Substitution[] = [];
      const nextErrors: Record<string, string> = {};
      for (let i = 0; i < subs.length; i++) {
        const s = subs[i]!;
        const min = Number(s.minute);
        if (!Number.isInteger(min) || min < 0) {
          nextErrors[`sub_${i}`] = 'Minute must be a non-negative integer.';
          continue;
        }
        builtSubs.push({ minute: min, direction: s.direction });
      }
      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors);
        setSubmitting(false);
        return;
      }
      patch.substitutions = builtSubs;
      // Always send positions (so an emptied list clears server-side)
      patch.positions = positions;
      // Drop the legacy single-position field — positions[] is canonical
      delete (patch as Record<string, unknown>).position;
      await api.update(kidId, matchId, patch);
      navigate(`/kids/${encodeURIComponent(kidId)}/matches/${encodeURIComponent(matchId)}`);
    } catch (err) {
      const e2 = err as ApiError;
      setErrors(e2.fields ?? { form: e2.message || 'Save failed.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <Link to={`/kids/${encodeURIComponent(kidId)}/matches/${encodeURIComponent(matchId)}`}
        style={{ color: 'var(--muted)', fontSize: 14 }}>← Cancel</Link>
      <h1 style={{ marginTop: 8, marginBottom: 16 }}>Edit match</h1>

      <Section title="Match info">
        <FormField label="Date" type="date" value={form.date ?? ''} onChange={(e) => set('date', e.target.value)} required error={errors.date} />
        <FormField label="Kick-off" type="time" value={form.kickoff ?? ''} onChange={(e) => set('kickoff', e.target.value)} error={errors.kickoff} />
        <FormField label="Opposition" value={form.opposition ?? ''} onChange={(e) => set('opposition', e.target.value)} error={errors.opposition} />

        <Field label="Match type">
          <ChipRow value={form.matchType ?? 'league'} options={MATCH_TYPES} onChange={(v) => set('matchType', v as MatchType)} />
        </Field>

        <Field label="Format (optional)">
          <ChipRow value={form.matchFormat ?? ''} options={MATCH_FORMATS}
            onChange={(v) => set('matchFormat', (v || undefined) as MatchFormat | undefined)} allowClear />
        </Field>

        <FormField label="Match title (optional)" value={form.matchTitle ?? ''} onChange={(e) => set('matchTitle', e.target.value)} hint="e.g. U9 Cup Final" error={errors.matchTitle} />
        <FormField label="Match length (mins)" type="number" min={1} max={200} value={form.matchLengthMin ?? ''} onChange={(e) => set('matchLengthMin', Number(e.target.value) || undefined)} error={errors.matchLengthMin} />

        <Field label="Home / away">
          <ChipRow value={form.homeAway ?? ''} options={['home', 'away']} onChange={(v) => set('homeAway', v as 'home' | 'away')} allowClear />
        </Field>
      </Section>

      <Section title="Result">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormField label="Our score" type="number" min={0} max={50} value={form.ourScore ?? ''} onChange={(e) => set('ourScore', e.target.value === '' ? undefined : Number(e.target.value))} />
          <FormField label="Their score" type="number" min={0} max={50} value={form.theirScore ?? ''} onChange={(e) => set('theirScore', e.target.value === '' ? undefined : Number(e.target.value))} />
        </div>
      </Section>

      <Section title="Performance">
        <Field label="Positions played (tap any that apply — they rotate)">
          <MultiChipRow values={positions} options={POSITIONS} onChange={setPositions} max={5} />
        </Field>
        <FormField label="Rating (1–10)" type="number" min={1} max={10} value={form.kidRating ?? ''} onChange={(e) => set('kidRating', e.target.value === '' ? undefined : Number(e.target.value))} />
        <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
          <Toggle label="Captain" value={!!form.captain} onChange={(v) => set('captain', v)} />
          <Toggle label="Man of the match" value={!!form.manOfMatch} onChange={(v) => set('manOfMatch', v)} />
        </div>

        <Field label="Substitutions (rolling)">
          <SubsList subs={subs} onChange={setSubs} errors={errors} />
        </Field>

        <FormField label="Minutes played (or skip and we'll trust the subs list)" type="number" min={0} value={form.minutesPlayed ?? ''} onChange={(e) => set('minutesPlayed', e.target.value === '' ? undefined : Number(e.target.value))} />
      </Section>

      <Section title="Note">
        <textarea
          value={form.note ?? ''}
          onChange={(e) => set('note', e.target.value)}
          placeholder="How was the match? (optional)"
          rows={4}
          style={{
            width: '100%',
            padding: '12px 16px',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            fontFamily: 'inherit',
            fontSize: 16,
            color: 'var(--ink)',
            resize: 'vertical',
          }}
        />
        {errors.note ? <p style={{ color: 'var(--error)', fontSize: 14, marginTop: 4 }}>{errors.note}</p> : null}
      </Section>

      {errors.form ? <p style={{ color: 'var(--error)', fontSize: 14, marginTop: 8 }}>{errors.form}</p> : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 20 }}>
        <Button label="Save changes" type="submit" loading={submitting} />
      </div>
    </form>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 24 }}>
      <h2 style={{ fontSize: 16, marginBottom: 12, fontFamily: 'var(--font-serif)', color: 'var(--green)' }}>{title}</h2>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 14, color: 'var(--muted)', marginBottom: 4, fontWeight: 500 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function ChipRow({
  value, options, onChange, allowClear,
}: { value: string; options: readonly string[]; onChange: (v: string) => void; allowClear?: boolean }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            type="button"
            key={opt}
            onClick={() => onChange(allowClear && active ? '' : opt)}
            style={{
              padding: '8px 14px',
              borderRadius: 999,
              border: `1px solid ${active ? 'var(--green)' : 'var(--border)'}`,
              background: active ? 'var(--green)' : 'transparent',
              color: active ? 'var(--ink-on-green)' : 'var(--ink)',
              fontFamily: 'inherit',
              fontSize: 14,
              cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function MultiChipRow({
  values, options, onChange, max,
}: { values: string[]; options: readonly string[]; onChange: (v: string[]) => void; max?: number }) {
  const toggle = (opt: string) => {
    if (values.includes(opt)) {
      onChange(values.filter((v) => v !== opt));
    } else {
      if (max && values.length >= max) return;
      onChange([...values, opt]);
    }
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map((opt) => {
        const active = values.includes(opt);
        return (
          <button
            type="button"
            key={opt}
            onClick={() => toggle(opt)}
            style={{
              padding: '8px 14px',
              borderRadius: 999,
              border: `1px solid ${active ? 'var(--green)' : 'var(--border)'}`,
              background: active ? 'var(--green)' : 'transparent',
              color: active ? 'var(--ink-on-green)' : 'var(--ink)',
              fontFamily: 'inherit',
              fontSize: 14,
              cursor: 'pointer',
              textTransform: 'capitalize',
              minHeight: 36,
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)}
        style={{ width: 18, height: 18, accentColor: 'var(--green)' }} />
      <span style={{ fontSize: 14, color: 'var(--ink)' }}>{label}</span>
    </label>
  );
}
